/**
 * Periodevalg for datofiltre: hurtigvalg («I dag», «Siste 7 dager» …) eller egendefinert
 * Fra/Til. Brukes av felleskomponenten DateRangeFilter.
 *
 * Datoene er lokale kalenderdager («yyyy-mm-dd») og klokkeslett «hh:mm». Uten klokkeslett
 * gjelder perioden hele dager: fra kl. 00:00 første dag til og med siste dag.
 */

export type DateRangePreset =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'lastWeek'
  | 'last7'
  | 'last30'
  | 'thisMonth'
  | 'lastMonth'
  | 'custom';

export interface DateRangeValue {
  preset: DateRangePreset;
  /** Første dag, «yyyy-mm-dd» (brukes ved preset «custom»; ellers regnet ut fra preset) */
  fromDate: string;
  /** Siste dag, «yyyy-mm-dd», til og med */
  toDate: string;
  /** Klokkeslett er slått på: perioden går fra fromTime til og med toTime */
  includeTime: boolean;
  /** «hh:mm» — brukes bare når includeTime er på */
  fromTime: string;
  toTime: string;
}

export const DATE_RANGE_PRESETS: DateRangePreset[] = ['today', 'yesterday', 'thisWeek', 'lastWeek', 'last7', 'last30', 'thisMonth', 'lastMonth'];

const pad = (n: number) => String(n).padStart(2, '0');

/** Lokal kalenderdag som «yyyy-mm-dd» (ikke toISOString, som gir UTC-dagen). */
export const toDateKey = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const fromDateKey = (key: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key || '');
  // Årstall utenfor 2000–2100 er som regel et halvskrevet felt (2 → «0002»); SQL datetime tåler ikke år før 1753
  if (!m || Number(m[1]) < 2000 || Number(m[1]) > 2100) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};

const addDays = (d: Date, days: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);

/** Mandagen i uka datoen ligger i (uka går mandag–søndag). */
const startOfWeek = (d: Date) => addDays(d, -((d.getDay() + 6) % 7));

/** Første og siste dag for et hurtigvalg, regnet fra «nå». */
export const presetDays = (preset: DateRangePreset, now: Date = new Date()): { fromDate: string; toDate: string } | null => {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case 'today':
      return { fromDate: toDateKey(today), toDate: toDateKey(today) };
    case 'yesterday': {
      const y = addDays(today, -1);
      return { fromDate: toDateKey(y), toDate: toDateKey(y) };
    }
    case 'thisWeek':
      return { fromDate: toDateKey(startOfWeek(today)), toDate: toDateKey(today) };
    case 'lastWeek': {
      const monday = addDays(startOfWeek(today), -7);
      return { fromDate: toDateKey(monday), toDate: toDateKey(addDays(monday, 6)) };
    }
    case 'last7':
      return { fromDate: toDateKey(addDays(today, -6)), toDate: toDateKey(today) };
    case 'last30':
      return { fromDate: toDateKey(addDays(today, -29)), toDate: toDateKey(today) };
    case 'thisMonth':
      return { fromDate: toDateKey(new Date(today.getFullYear(), today.getMonth(), 1)), toDate: toDateKey(today) };
    case 'lastMonth': {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      return { fromDate: toDateKey(first), toDate: toDateKey(last) };
    }
    default:
      return null;
  }
};

export const createDateRangeValue = (preset: DateRangePreset = 'last7', now: Date = new Date()): DateRangeValue => {
  const days = presetDays(preset, now) ?? presetDays('last7', now)!;
  return { preset, ...days, includeTime: false, fromTime: '00:00', toTime: '23:59' };
};

/**
 * Dagene som faktisk gjelder: et hurtigvalg regnes alltid fra dagens dato, så «I dag»
 * betyr i dag også når siden har stått åpen siden i går.
 */
export const effectiveDays = (value: DateRangeValue, now: Date = new Date()): { fromDate: string; toDate: string } =>
  (value.preset !== 'custom' && presetDays(value.preset, now)) || { fromDate: value.fromDate, toDate: value.toDate };

const isTime = (t: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(t || '');

/** Feilkode når perioden ikke kan brukes; null når den er gyldig. */
export const validateDateRange = (value: DateRangeValue, now: Date = new Date()): 'missing' | 'invalidTime' | 'fromAfterTo' | null => {
  const { fromDate, toDate } = effectiveDays(value, now);
  const from = fromDateKey(fromDate);
  const to = fromDateKey(toDate);
  if (!from || !to) return 'missing';
  if (value.includeTime && (!isTime(value.fromTime) || !isTime(value.toTime))) return 'invalidTime';
  const range = resolveDateRange(value, now);
  if (range.from.getTime() > range.to.getTime()) return 'fromAfterTo';
  return null;
};

/**
 * Perioden som Date-par. Uten klokkeslett: from = første dag kl. 00:00, to = siste dag kl. 00:00
 * (backend tolker midnatt som «hele dagen»). Med klokkeslett: to = valgt minutt kl. ss = 59,
 * slik at perioden er til og med det minuttet.
 */
export const resolveDateRange = (value: DateRangeValue, now: Date = new Date()): { from: Date; to: Date } => {
  const { fromDate, toDate } = effectiveDays(value, now);
  const from = fromDateKey(fromDate) ?? new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const to = fromDateKey(toDate) ?? new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (value.includeTime && isTime(value.fromTime) && isTime(value.toTime)) {
    const [fh, fm] = value.fromTime.split(':').map(Number);
    const [th, tm] = value.toTime.split(':').map(Number);
    from.setHours(fh, fm, 0, 0);
    to.setHours(th, tm, 59, 0);
  }
  return { from, to };
};

/** Lokal tid uten tidssone, «yyyy-mm-ddThh:mm:ss» — for API-parametre (ikke UTC). */
export const toLocalDateTimeParam = (d: Date): string =>
  `${toDateKey(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

/** Antall kalenderdager i perioden (minst 1). */
export const dayCount = (value: DateRangeValue, now: Date = new Date()): number => {
  const { fromDate, toDate } = effectiveDays(value, now);
  const from = fromDateKey(fromDate);
  const to = fromDateKey(toDate);
  if (!from || !to) return 0;
  return Math.max(1, Math.round((to.getTime() - from.getTime()) / 86400000) + 1);
};

export type PeriodStep = 'week' | 'month';

/**
 * Om perioden er en hel uke (mandag–søndag) eller en hel måned, slik at den kan blas med piler
 * (TW 36443153). «Denne uken»/«Denne måneden» går bare til i dag, men regnes som hel periode.
 * Andre perioder, og perioder med klokkeslett, kan ikke blas.
 */
export const periodStep = (value: DateRangeValue): PeriodStep | null => {
  if (value.includeTime) return null;
  if (value.preset === 'thisWeek' || value.preset === 'lastWeek') return 'week';
  if (value.preset === 'thisMonth' || value.preset === 'lastMonth') return 'month';
  if (value.preset !== 'custom') return null;
  const from = fromDateKey(value.fromDate);
  const to = fromDateKey(value.toDate);
  if (!from || !to) return null;
  if (from.getDay() === 1 && toDateKey(addDays(from, 6)) === toDateKey(to)) return 'week';
  if (from.getDate() === 1 && toDateKey(new Date(from.getFullYear(), from.getMonth() + 1, 0)) === toDateKey(to)) return 'month';
  return null;
};

/**
 * Perioden én uke/måned bakover (-1) eller fremover (1). Treffer resultatet inneværende eller
 * forrige uke/måned, brukes hurtigvalget, så knappen lyser og «Denne uken» følger dagens dato.
 * Gir null når perioden ikke kan blas, eller når neste periode ligger helt i fremtiden.
 */
export const shiftPeriod = (value: DateRangeValue, direction: -1 | 1, now: Date = new Date()): DateRangeValue | null => {
  const step = periodStep(value);
  const from = fromDateKey(effectiveDays(value, now).fromDate);
  if (!step || !from) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const first = step === 'week'
    ? addDays(from, 7 * direction)
    : new Date(from.getFullYear(), from.getMonth() + direction, 1);
  if (first.getTime() > today.getTime()) return null;
  const last = step === 'week' ? addDays(first, 6) : new Date(first.getFullYear(), first.getMonth() + 1, 0);

  const fromDate = toDateKey(first);
  const presets: DateRangePreset[] = step === 'week' ? ['thisWeek', 'lastWeek'] : ['thisMonth', 'lastMonth'];
  const preset = presets.find(p => presetDays(p, now)?.fromDate === fromDate);
  return preset
    ? { ...value, preset, ...presetDays(preset, now)!, includeTime: false }
    : { ...value, preset: 'custom', fromDate, toDate: toDateKey(last), includeTime: false };
};

/** ISO-ukenummer (uke 1 er uka med årets første torsdag) og året uka hører til. */
export const isoWeek = (d: Date): { week: number; year: number } => {
  const thursday = addDays(startOfWeek(d), 3);
  const jan1 = new Date(thursday.getFullYear(), 0, 1);
  return { week: Math.floor(Math.round((thursday.getTime() - jan1.getTime()) / 86400000) / 7) + 1, year: thursday.getFullYear() };
};

/** Første dag i perioden som Date (for visning av uke/måned). */
export const periodStart = (value: DateRangeValue, now: Date = new Date()): Date | null =>
  fromDateKey(effectiveDays(value, now).fromDate);

/** Leser et lagret periodevalg; ugyldig eller manglende verdi gir standard (siste 7 dager). */
export const parseStoredDateRange = (raw: string | null, now: Date = new Date()): DateRangeValue => {
  const fallback = createDateRangeValue('last7', now);
  if (!raw) return fallback;
  try {
    const v = JSON.parse(raw) as Partial<DateRangeValue>;
    const preset = (DATE_RANGE_PRESETS as string[]).includes(v.preset as string) || v.preset === 'custom'
      ? (v.preset as DateRangePreset)
      : 'last7';
    const value: DateRangeValue = {
      ...fallback,
      preset,
      fromDate: typeof v.fromDate === 'string' ? v.fromDate : fallback.fromDate,
      toDate: typeof v.toDate === 'string' ? v.toDate : fallback.toDate,
      includeTime: v.includeTime === true,
      fromTime: isTime(v.fromTime as string) ? (v.fromTime as string) : fallback.fromTime,
      toTime: isTime(v.toTime as string) ? (v.toTime as string) : fallback.toTime,
    };
    return validateDateRange(value, now) === null ? value : fallback;
  } catch {
    return fallback;
  }
};
